import { PencilIcon } from "@heroicons/react/outline";
import { CurlyBracesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useWorkflow } from "../workflow-context";

import WorkflowInputVariableConfigurator from "./Controls/InputVariables/WorkflowInputVariableConfigurator";

import { BasicTooltip } from "@/components/ui/Tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface InputVariablesFooterProps {
  readonly?: boolean;
}

const InputVariablesFooter = ({ readonly }: InputVariablesFooterProps) => {
  const workflow = useWorkflow();
  const { input_variables } = workflow;
  const [isOpen, setIsOpen] = useState(false);

  const hasVariables = input_variables && input_variables.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex w-full flex-row items-center gap-2 overflow-x-auto overflow-y-hidden border-t border-gray-200 bg-white px-4 py-4">
        {hasVariables ? (
          <div className="flex flex-1 flex-row items-center gap-2 overflow-x-auto">
            {!readonly && (
              <BasicTooltip content="Edit Input Variables">
                <DialogTrigger asChild>
                  <button className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </DialogTrigger>
              </BasicTooltip>
            )}
            {input_variables.map(variable => (
              <span key={variable.key} className="input-variable whitespace-nowrap">
                {`{${variable.key}}`}
              </span>
            ))}
          </div>
        ) : (
          <>
            {!readonly ? (
              <DialogTrigger asChild>
                <Button variant="default" className="shrink-0">
                  <CurlyBracesIcon className="mr-2 h-4 w-4" />
                  Add Input Variables
                </Button>
              </DialogTrigger>
            ) : (
              <span className="text-sm text-gray-500">No input variables</span>
            )}
          </>
        )}
      </div>
      <DialogContent className="flex h-[580px] max-h-[80vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{hasVariables ? "Edit Input Variables" : "Add Input Variables"}</DialogTitle>
          <DialogDescription>
            Input variables allow you to create reusable values that can be referenced in your
            nodes. Use the syntax <code>{"{variable}"}</code> to include variables in your text.
            <p className="mt-1 text-sm text-gray-600">
              Note: Only variable names are saved. Values are only used at runtime preview.
            </p>
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <WorkflowInputVariableConfigurator onClose={() => setIsOpen(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default observer(InputVariablesFooter);
